














import { defineComponent, onMounted, ref, watch } from "@vue/composition-api";
import usePlayer from "@/hooks/usePlayer";
import appStorage from "@/lib/appStorage";
import { pauseGlobalPlayer } from "@/components/GlobalAudioPlayer/helpers";

interface StoredVolume {
  volume: number;
  muted: boolean;
}

export default defineComponent({
  name: "BaseVideo",
  props: {
    src: { type: String, required: true },
    muted: { type: Boolean, default: false },
    autoplay: { type: Boolean, default: false },
    allowSystemFullscreen: { type: Boolean, default: false },
    volumeStorageKey: { type: String, default: "" },
  },
  setup(props, { emit }) {
    const player = ref<HTMLVideoElement>();
    const storedVolume = props.volumeStorageKey ? appStorage.getObject<StoredVolume>(props.volumeStorageKey) : null;
    const isMuted = ref(storedVolume?.muted ?? props.muted);

    let ignoreVolumeChange = false;

    const { isMediaPlaying: videoPlaying } = usePlayer(player);

    const onVolumeChange = () => {
      if (!player.value || !props.volumeStorageKey || ignoreVolumeChange) return;

      appStorage.setObject(props.volumeStorageKey, {
        volume: player.value.volume,
        muted: player.value.muted,
      });
    };

    const playWithSoundFallback = async () => {
      if (!player.value) return;
      player.value.muted = isMuted.value;
      try {
        await player.value.play();
      } catch {
        if (!player.value || isMuted.value) return;
        ignoreVolumeChange = true;
        isMuted.value = true;
        player.value.muted = true;
        try {
          await player.value.play();
          ignoreVolumeChange = false;
        } catch {
          // ignore
        }
      }
    };

    watch(
      () => props.muted,
      v => {
        isMuted.value = v;
        if (player.value) player.value.muted = v;
      }
    );

    watch(videoPlaying, () => {
      emit("changeVideoPlayingStatus", videoPlaying.value);
    });

    const unwatch = watch(videoPlaying, () => {
      pauseGlobalPlayer();
      unwatch();
    });

    onMounted(() => {
      if (!player.value) return;
      if (storedVolume) {
        player.value.volume = storedVolume.volume;
        player.value.muted = storedVolume.muted;
      }
      player.value.addEventListener("volumechange", onVolumeChange);
      if (props.autoplay) {
        playWithSoundFallback();
      }
    });

    return {
      player,
      isMuted,
    };
  },
});
